import {
  DeliverMedicationConsultationUpsell,
  DoctorsNoteConsultationUpsell,
  FluconazoleConsultationUpsell,
  FollowUpUrineTestConsultationUpsell,
  LevonorgestrelConsultationUpsell,
  MensIntimateWashConsultationUpsell,
  OndansetronDissolvableTabletStdConsultationUpsell,
  OndansetronPillStdConsultationUpsell,
  PriorityProcessingConsultationUpsell,
  ProbioticsConsultationUpsell,
  PyridiumConsultationUpsell,
} from '@data/consultation-request-upsells';
import { Pharmacy } from '@enums/pharmacies';
import { Sites } from '@enums/sites';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { AppEnvironment } from '@models/app-environment';

/**
 * Environment variables for use on the TreatMyUTI.com site, both in production and local environment.
 */
export const treatmyutiCommonEnvironment = {
  site: Sites.Treatmyuti,
  siteId: '17',
  domain: 'TreatMyUTI.com',
  faviconFile: 'treatmyuti-favicon.ico',
  phone: '1-866-511-3060',
  email: 'support@treatmyuti.com',
  businessHours: 'Monday - Sunday 8 a.m. - 8 p.m. (Central Standard Time - CST)',
  allowedRoutes: [
    '/order',
    '/female-condom',
    '/order-complete.php',
    '/null',
    '/consultation/request',
    '/consultation/upload-consult-attachments',
    '/consultation/yeast-infection-prevention',
    '/consultation/alleviate-nausea-and-vomiting',
    '/consultation/replenish-your-good-bacteria',
    '/consultation/mens-intimate-wash',
    '/consultation/need-a-doctors-note',
    '/consultation/be-prepared-for-the-unexpected',
    '/consultation/medication-pickup-or-delivery',
    '/consultation/payment-info',
    '/consultation/schedule-consultation',
    '/consultation/std/request',
    '/consultation/std-prevention/request',
    '/consultation/uti/request',
    '/consultation/vaginal-dryness/request',
    '/uti/consultation-request-completed',
    '/consultation/submission-error',
    '/consultation/priority-processing',
  ],
  enablePayLater: false,
  enableBitPay: false,
  enableGiftCard: true,
  enableBusinessAccounts: false,
  applePayLabel: 'Analyte Health',
  order: {
    minAgeAllowed: 18,
    showCancellationPolicy: false,
    navigateToOrderAddressRoute: false,
    shouldShowPhoneFieldOnOrderRoute: true,
    hasPhysicianFee: false,
    physicianFeeInCents: 0,
    forceEmailResults: true,
    isPartnerTestingAllowed: false,
    orderPaymentFormLayout: 'stacked',
    enableOrderUpgrade: false,
    enableDynamicOrderUpgrade: false,
    enableTosOnOrderPage: true,
    stdPanelWithMycoplasmaTestId: 3162,
    abTestsWithMycoplasmaTestId: [3377, 3379, 3376],
    comprehensiveWellnessPanelId: 3381,
    isFemaleCondomUpsellAllowed: true,
    triggerFemaleCondomUpsellConsultation: false,
    skipUpsellsForBusinessAccounts: false,
    orderSummary: {
      allowEditingCart: true,
      editCartButtonText: 'Edit',
      displayCartSummaryTableHeaders: false,
      testsPrefix: 'UTI Antibiotics Online',
      showCouponCode: false,
      displayFindTestsModal: false,
    },
    orderComplete: {
      displaySisterSiteBanner: false,
      testOrderedWording: {
        single: 'Preferred Medication/Treatment',
        plural: 'Preferred Medications/Treatments',
      },
    },
  },
  maxAgeAllowed: 81,
  minimumNameLength: 2,
  disableSchedulingRedirectForTreatmentTypes: [],
  consultation: {
    deliveryPharmacyId: Pharmacy.TAHA,
    lastPeriodQuestionId: '97',
    erectileDysfunctionMedicationQuestionId: '234',
    offersEdTreatmentUpsell: false,
    highBloodPressureQuestionId: '228',
    breastfeedingQuestionIds: ['31', '82'],
  },
  ngFindalab: {
    findalabMapId: '8f1673661122dda0',
    searchEndpoint: '/pharmacies/labs/nearPostalCode',
    filterByStates: null,
    inHomeCollectionAllowed: false,
    inHomeCollectionCostInCents: 11900,
    inHomeCollectionProviderIds: [1, 2],
    showPhone: true,
    filterByTestAndSite: false,
    getNetworksFromTests: false,
    verifyTestsCanBeOrdered: false,
    validateSelectedLabNetwork: false,
    searchTitle: 'Enter Your Location',
    searchDescription:
      'Please choose a nearby pharmacy where our clinician can send your prescription once they have reviewed your medical information and your order has been placed.',
    pharmacySearchDescription:
      'Please choose a nearby pharmacy where our clinician can send your prescription once they have reviewed your medical information.',
    locateMsg: 'Use my current location',
    labButtonText: 'CHOOSE THIS PHARMACY',
    searchButtonText: 'Find',
    showSearchIcon: false,
    labIconClass: 'fa fa-arrow-right',
    searchMessageNoResults:
      'Oops! Sorry, we could not find any pharmacies near that location. Please try your search again with a different or less specific address.',
    inputGroup: {
      container: '',
      field: null,
      button: '',
    },
    resultsZoom: 12,
    choosePharmacySearchTitle: 'Enter Your Location',
    showDayOfWeekFilter: false,
  },
  titles: {
    order: 'UTI Checkout - TreatMyUTI.com',
    consultationRequest: 'Questionnaire - TreatMyUTI.com',
    headlineCheckoutScheduled: '',
    step1Title: 'Find a Pharmacy',
    selectedCenterLabel: 'Pharmacy',
    orderPageHeader: {
      title: "You're almost <strong>Done!</strong>",
      subtitle: 'We just need a few more details before determining your diagnosis.',
    },
    scheduledConsultationRequest: {
      title: 'Clinician Phone Consultation Needed',
      calendarDetails:
        'To ensure compliance with Telehealth regulations in your state, we must arrange a brief phone consultation with one of our clinicians before prescribing medication. This consultation is provided at no additional cost to you.',
      phoneNumberDetails: 'A Clinician will call you at',
      availabilityDetails:
        "Typically, our clinicians call earlier than the anticipated window, so there's no need to be concerned if your appointment appears to be scheduled further than expected.",
    },
  },
  buttons: {
    checkout: 'Place Order',
    selectedCenterChange: 'Change Pharmacy',
  },
  styles: {
    metaThemeColor: '#fdd8d9',
    chooseAPharmacy: {
      labButtonClass: 'is-secondary-orange has-text-white',
      searchButtonClass: 'is-green has-text-white',
      googleMapsLabMarkerFillColor: '#b46397',
      googleMapsRecommendedMarkerFillColor: '#fdd8d9',
      googleMapsMarkerHoverFillColor: '#cfb0c8',
    },
    consultationRequest: {
      headlineClass: 'column is-flex is-justify-content-center pt-5 pb-0 has-text-weight-semibold',
    },
    healthLabsCoupon: {
      buttonColor: 'secondary-orange',
    },
    orderCompleteInstructions: {
      cardContentClass: 'has-background-green-light',
    },
    orderBetterLab: {
      orderPlaceNotificationClass: 'is-warning',
      currentTestCenterNotificationClass: 'is-info',
      keepCurrentLabButtonClass: 'is-uppercase',
      keepCurrentLabButtonColor: 'secondary-orange',
    },
    testCenter: {
      labButtonClass: '',
      searchButtonClass: 'is-secondary-orange has-text-white',
      googleMapsLabMarkerFillColor: '#b46397',
      googleMapsRecommendedMarkerFillColor: '#fdd8d9',
      googleMapsMarkerHoverFillColor: '#cfb0c8',
    },
  },
  shopperApproved: {
    site: 25125,
    token: '2dbCcwg6',
  },
  consultationRequestUpsells: {
    [UpsellSlugs.Pyridium]: PyridiumConsultationUpsell,
    [UpsellSlugs.FollowUpUrineTest]: FollowUpUrineTestConsultationUpsell,
    [UpsellSlugs.Fluconazole]: FluconazoleConsultationUpsell,
    [UpsellSlugs.Probiotics]: ProbioticsConsultationUpsell,
    [UpsellSlugs.DoctorsNote]: DoctorsNoteConsultationUpsell,
    [UpsellSlugs.Levonorgestrel]: LevonorgestrelConsultationUpsell,
    [UpsellSlugs.DeliverMedication]: DeliverMedicationConsultationUpsell,
    [UpsellSlugs.OndansetronPill]: OndansetronPillStdConsultationUpsell,
    [UpsellSlugs.OndansetronDissolvableTablet]: OndansetronDissolvableTabletStdConsultationUpsell,
    [UpsellSlugs.MensIntimateWash]: MensIntimateWashConsultationUpsell,
    [UpsellSlugs.PriorityProcessing]: PriorityProcessingConsultationUpsell,
  },
} satisfies Partial<AppEnvironment>;
